import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { SiteFormsService } from 'app/_services/site-forms.service';
import { SiteOwnerUserService } from 'app/_services/site-owner-user.service';
import { DragulaService } from 'ng2-dragula';
import Swal from 'sweetalert2';

@Component({
  selector: 'site-forms-create',
  templateUrl: './site-forms-create.component.html',
})
export class SiteFormsCreateComponent implements OnInit, OnDestroy {
  private routeRole: any;

  public isEditExistingForm = false;

  public formName = '';
  public siteOwner: any;
  public destinationFields = [];
  public linkedSites = [];

  public isDialogOpen = false;
  public jsonError = false;

  // don't use the following as values, or the simple cloning method in copyItem won't copy the object right:
  // --- Dates, functions, undefined, Infinity, RegExps, Maps, Sets, Blobs, FileLists, ImageDatas, sparse Arrays, Typed Arrays or other complex types
  private baseField = { label: '', required: false };

  public sourceFieldsInput = [
    { type: 'textField', category: 'input', ...this.baseField },
    { type: 'textArea', category: 'input', ...this.baseField },
    { type: 'dateField', category: 'input', ...this.baseField },
    { type: 'timeField', category: 'input', ...this.baseField },
    { type: 'radioButton', category: 'input', ...this.baseField, options:['Yes', 'No'] },
    { type: 'checkboxList', category: 'input', ...this.baseField, options:['Option A', 'Option B', 'Option C'] },
    { type: 'table', category: 'table-input', ...this.baseField, fields: [{ "type": "textField", category: 'input', ...this.baseField }] },
  ];

  public sourceFieldsInformational = [
    { type: 'headerLarge', category: 'informational', text: '' },
    { type: 'headerSmall', category: 'informational', text: ''  },
    { type: 'paragraphOfText', category: 'informational', textarea: '' },
  ];

  public sourceFieldsAttachment = [
    { type: 'imageUpload', category: 'attachment', ...this.baseField },
    { type: 'documentUpload', category: 'attachment', ...this.baseField },
  ];

  public frontendOnlySourceFieldTypeLabel = {
    textField: 'Text Field',
    textArea: 'Text Field - Multiline',
    dateField: 'Date Field',
    timeField: 'Time Field',
    radioButton: 'Multiple Choice - One answer',
    checkboxList: 'Multiple Choice - Many answers',
    imageUpload: 'Image Upload',
    documentUpload: 'Document Upload',
    table: 'Table',
    headerLarge: 'Header - Large',
    headerSmall: 'Header - Small',
    paragraphOfText: 'Paragraph of Text',
  };

  siteFormId: any;
  public isSubmitting = false;
  public validationErrors: any[] = [];
  public isSiteOwner: boolean;
  public siteOwners = [];

  constructor(
    private dragulaService: DragulaService,
    private siteFormsService: SiteFormsService,
    private siteOwnerUserService: SiteOwnerUserService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private globals: Globals,
  ) {
    this.routeRole = globals.user.routeRole;
    this.isSiteOwner = this.routeRole === 'site-owner';

    this.setUpDragula();

    this.activatedRoute.params.subscribe((params: Params) => {
      this.siteFormId = params.id;
      this.isEditExistingForm = !!this.siteFormId;
    });
  }

  ngOnInit() {
    if (this.isEditExistingForm) {
      this.siteFormsService.getSiteForm(this.siteFormId).subscribe(response => {
        this.formName = response.name;
        this.destinationFields = response.data;
        this.linkedSites = response.linkedSites.map(site => site.siteId);
      });
    }

    // Get Site Owners
    if (!this.isSiteOwner) {
      this.siteOwnerUserService.getAllEnabledSiteOwnersForDropdowns().subscribe((data: any) => {
        data.forEach(element => {
          const obj = {
            label: element.firstName + ' ' + element.lastName,
            value: element.id
          };
          this.siteOwners.push(obj);
        });
        this.siteOwner = this.siteOwners[0].value;
      });
    }
  }

  ngOnDestroy() {
    this.dragulaService.destroy('FORM_CREATOR');
  }

  private setUpDragula() {
    this.dragulaService.createGroup("FORM_CREATOR", {
      copySortSource: true,
      copy: (el, source) => {
        return source.id === 'source';
      },
      copyItem: (sourceObj) => {
        // clones simple json objects
        return JSON.parse(JSON.stringify(sourceObj));
      },
      accepts: (el, target, source, sibling) => {
        // don't allow nestables inside nestables
        const elementIsContainer = el.getAttribute('data-container-type') === 'true';
        const elementIsNestable = el.getAttribute('data-nestable') === 'true';
        if (target.id === 'container-type' && (elementIsContainer || !elementIsNestable)) {
          return false;
        }

        // don't allow nestable inside itself
        if (el === target) {
          return false
        }
        // don't allow copy back to the source list
        return target.id !== 'source';
      }
    });
  }

  public removeMultipleChoiceOption(item, index) {
    item.options.splice(index, 1);
  }

  public addMultipleChoiceOptionTo(item) {
    item.options.push('');
  }

  // helps tell the page when to reload when the items in the array are changed.
  public trackByIndex(index: number): any {
    return index;
  }

  public removeFormElement(item, parent) {
    let indexOfItem = parent.findIndex(element => element === item);

    if (indexOfItem > -1) {
      parent.splice(indexOfItem, 1);
    }
  }

  public openDialog() {
    this.isDialogOpen = true;
    this.jsonError = false;
  }

  public updateJsonDirectly(jsonText) {
    try {
      let newJson = JSON.parse(jsonText);
      if (!Array.isArray(newJson)) {
        throw new Error();
      }

      this.destinationFields = newJson;
      this.jsonError = false;
    } catch (error) {
      this.jsonError = true;
    }
  }

  public submitForm() {
    this.isSubmitting = true;
    let body = {
      name: this.formName,
      formData: JSON.stringify(this.destinationFields),
    }

    let isValid = this.validateForm();
    if (!isValid) {
      this.isSubmitting = false;
      return
    }

    if (!this.isEditExistingForm) {
      // Create new form

      if (!this.isSiteOwner) {
        body['skylarkUserId'] = this.siteOwner;
      } else {
        body['skylarkUserId'] = this.globals.user.id;
      }

      this.siteFormsService.createForm(body).subscribe(response => {
        Swal.fire('Success!', 'New Form Created.<br>Now link it to the desired sites', 'success');

        this.isSubmitting = false;
        this.router.navigate(['/', this.routeRole, 'site-forms', response.id]);
      },
      error => {
        this.isSubmitting = false;
        throw error;
      });
    } else {
      // Edit form

      body['sites'] = this.linkedSites;

      this.siteFormsService.updateForm(this.siteFormId, body).subscribe(response => {
        Swal.fire('Success!', 'Form updated.', 'success');

        this.isSubmitting = false;
        this.router.navigate(['/', this.routeRole, 'site-forms', response.id]);
      },
      error => {
        this.isSubmitting = false;
        throw error;
      });
    }
  }

  private validateForm() {
    this.validationErrors = [];
    let validationErrors = [];

    if (this.formName.length < 1) {
      validationErrors.push({ message: 'The form needs a name' });
    }

    if (this.formName.length > 36) {
      validationErrors.push({ message: 'The form name is too long' });
    }

    let length = JSON.stringify(this.destinationFields).length;
    if (length >= 65535) {
      validationErrors.push({ message: 'This form is too large to save. Try shortening parts of the text or removing surplus fields.' });
    }

    if (this.destinationFields.length < 1) {
      validationErrors.push({ message: 'The form is empty' });
    }

    for (let i = 0; i < this.destinationFields.length; i++) {
      const field = this.destinationFields[i];

      // does field has a label
      if (field.category === 'input' || field.category === 'table-input') {
        if (!field.label) {
          validationErrors.push({ message: `Field ${i+1} is missing a question` });
        }
      } else if (field.category === 'informational') {
        if (!field.text && !field.textarea) {
          validationErrors.push({ message: `Field ${i+1} is missing text` });
        }
      }

      // radio and checkbox options
      if (field.type === 'radioButton' || field.type === 'checkboxList') {

        if (field.options.length  < 2) {
          validationErrors.push({ message: `Field ${i+1} is multiple choice and needs more options` });
        } else if (!field.options.every(o => o && o.length > 0)) {
          validationErrors.push({ message: `Field ${i+1} is missing some text in the options` });
        }
      }

      // table inner fields
      if (field.category === 'table-input') {

        if (!field.fields.length) {
          validationErrors.push({ message: `Field ${i + 1} is a table with no columns. Add some input fields` });
        }

        for (let j = 0; j < field.fields.length; j++) {
          let f = field.fields[j];

          if (!f.label) {
            validationErrors.push({ message: `Field ${i+1} is missing some column headings` });
            break;
          }

          // radio and checkbox options
          if (f.type === 'radioButton' || f.type === 'checkboxList') {

            if (f.options.length < 2) {
              validationErrors.push({ message: `Field ${i + 1} has a multiple choice column which needs more options` });
              break;
            } else if (!f.options.every(o => o && o.length > 0)) {
              validationErrors.push({ message: `Field ${i + 1} has a multiple choice column which is missing some text in the options` });
              break;
            }
          }
        }
      }
    }

    if (!validationErrors.length) {
      return true;
    } else {
      this.validationErrors = validationErrors;
      return false;
    }
  }
}
